import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { formatDate } from "@/shared/lib/date-time";
import { getInitials } from "@/shared/lib/utils";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { QueryState } from "@/shared/ui/QueryState";
import {
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  Card,
  CardContent,
  confirm,
  toast,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  CheckIcon,
  EditIcon,
  InfoIcon,
  LightbulbIcon,
  PowerIcon,
  TrashIcon,
  UsersIcon,
} from "lucide-react";
import {
  useClassTemplateEligibleInstructorsQuery,
  useClassTemplateQuery,
  useClassTemplateUsageStatsQuery,
  useDeleteClassTemplate,
  useToggleClassTemplateStatus,
} from "../api/queries";

export function ClassTemplateDetailPage() {
  const nav = useNavigate({
    from: "/app/classes/templates/$id",
  });
  const { id } = useParams({ from: "/app/classes/templates/$id" });
  const { data, ...result } = useClassTemplateQuery(id);
  const { data: usageStats } = useClassTemplateUsageStatsQuery(id);
  const { data: eligibleInstructors } =
    useClassTemplateEligibleInstructorsQuery(id);
  const toggleStatus = useToggleClassTemplateStatus(id);
  const deleteTemplate = useDeleteClassTemplate();

  const template = data;

  const handleToggleStatus = async () => {
    if (!template) return;

    const newStatus = template.status === "active" ? "inactive" : "active";
    const statusLabel = newStatus === "active" ? "ເປີດການໃຊ້ງານ" : "ປິດການໃຊ້ງານ";

    await toast.promise(toggleStatus.run(newStatus), {
      loading: "ກຳລັງປ່ຽນສະຖານະ...",
      success: `${statusLabel} template ສຳເລັດ`,
      error: "ປ່ຽນສະຖານະ template ລົ້ມເຫຼວ",
    });
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "Template ຄລາດສ໌", to: "/app/classes/templates" },
            { label: "ລາຍລະອຽດ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="font-bold text-3xl tracking-tight">
              {template
                ? `${template.classTypeName} - ${template.levelName}`
                : "Loading..."}
            </h1>
            {template && (
              <p className="mt-1 text-muted-foreground text-sm">
                Template ID: #{template.id.slice(0, 8).toUpperCase()}
              </p>
            )}
          </div>
          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/classes/templates" })}
          >
            <ArrowLeftIcon className="size-4" />
            ກັບຄືນລາຍການ
          </Button>
        </div>

        <QueryState
          result={result}
          title="ກຳລັງໂຫຼດ template"
          description="ກຳລັງດຶງລາຍລະອຽດ"
          variant="fullscreen"
        >
          {!template ? null : (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              {/* Left Column - Main Content */}
              <div className="space-y-6 lg:col-span-2">
                {/* Usage Statistics Card */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="rounded-lg border p-4">
                    <p className="mb-1 font-medium text-muted-foreground text-xs uppercase tracking-wide">
                      ຈອງແລ້ວ
                    </p>
                    <p className="font-bold text-2xl">
                      {usageStats?.scheduled.toLocaleString("lo-LA") ?? 0}
                    </p>
                    <p className="mt-1 text-muted-foreground text-xs">
                      ຄັ້ງຖັດໄປໃນ 2 ວັນ
                    </p>
                  </div>
                  <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                    <p className="mb-1 font-medium text-muted-foreground text-xs uppercase tracking-wide">
                      ເຂົ້າຮຽນແລ້ວ
                    </p>
                    <p className="font-bold text-2xl text-green-700">
                      {usageStats?.completed.toLocaleString("lo-LA") ?? 0}
                    </p>
                    <p className="mt-1 text-muted-foreground text-xs">
                      {usageStats && usageStats.total > 0
                        ? `${Math.round(
                          (usageStats.completed / usageStats.total) * 100,
                        )}% ອັດຕາການເຂົ້າຮ່ວມ`
                        : "0% ອັດຕາການເຂົ້າຮ່ວມ"}
                    </p>
                  </div>
                  <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                    <p className="mb-1 font-medium text-muted-foreground text-xs uppercase tracking-wide">
                      ຍົກເລີກ
                    </p>
                    <p className="font-bold text-2xl text-red-700">
                      {usageStats?.cancelled.toLocaleString("lo-LA") ?? 0}
                    </p>
                    <p className="mt-1 text-muted-foreground text-xs">
                      {usageStats && usageStats.total > 0
                        ? `${Math.round(
                          (usageStats.cancelled / usageStats.total) * 100,
                        )}% ອັດຕາການຍົກເລີກ`
                        : "0% ອັດຕາການຍົກເລີກ"}
                    </p>
                  </div>
                </div>{" "}
                {/* General Information Card */}
                <Card>
                  <CardContent className="px-6">
                    <div className="mb-4 flex items-center justify-between border-b pb-4">
                      <div className="flex items-center gap-2">
                        <InfoIcon className="size-5 text-red-600" />
                        <h3 className="font-semibold text-lg">ຂໍ້ມູນທົ່ວໄປ</h3>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            nav({
                              to: "/app/classes/templates/$id/edit",
                              params: { id },
                            })
                          }
                        >
                          <EditIcon className="size-4" />
                          ແກ້ໄຂ
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleToggleStatus}
                        >
                          <PowerIcon className="size-4" />
                          {template.status === "active"
                            ? "ປິດການໃຊ້ງານ"
                            : "ເປີດການໃຊ້ງານ"}
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={async () => {
                            const ok = await confirm({
                              title: "ລຶບ Template ຄລາດສ໌",
                              description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບ template ນີ້?",
                              actionText: "ລຶບ",
                              ActionProps: {
                                variant: "destructive",
                              },
                            });
                            if (!ok) return;

                            try {
                              await toast.promise(deleteTemplate.run(id), {
                                loading: "ກຳລັງລຶບ...",
                                success: () => {
                                  nav({ to: "/app/classes/templates" });
                                  return "ລຶບ template ສຳເລັດ";
                                },
                                error: "ລຶບ template ລົ້ມເຫຼວ",
                              });
                            } catch {
                              // Error handling is done by toast.promise
                            }
                          }}
                        >
                          <TrashIcon className="size-4" />
                          ລຶບ
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      <div>
                        <p className="text-muted-foreground text-sm">
                          ປະເພດຄລາດສ໌
                        </p>
                        <p className="font-semibold">
                          {template.classTypeName}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-sm">ລະດັບ</p>
                        <p className="font-semibold">{template.levelName}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-sm">
                          ລາຍການ (ນາທີ)
                        </p>
                        <p className="font-semibold">{template.duration} ນາທີ</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-sm">ສະຖານະ</p>
                        <Badge
                          variant={
                            template.status === "active"
                              ? "default"
                              : "secondary"
                          }
                        >
                          {template.status === "active" ? "ໃຊ້ງານ" : "ບໍ່ໃຊ້ງານ"}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-sm">
                          ລົງທະບຽນອອນລາຍ
                        </p>
                        <Badge
                          variant={
                            template.onlineRegistration ? "default" : "outline"
                          }
                        >
                          {template.onlineRegistration ? "ເປີດຮັບ" : "ປິດຮັບ"}
                        </Badge>
                      </div>
                      {template.description && (
                        <div className="md:col-span-2">
                          <p className="text-muted-foreground text-sm">
                            ຄຳອະທິບາຍ
                          </p>
                          <p className="font-semibold">
                            {template.description}
                          </p>
                        </div>
                      )}
                      <div>
                        <p className="text-muted-foreground text-sm">ວັນທີສ້າງ</p>
                        <p className="font-semibold">
                          {formatDate(template.createdAt)}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-sm">
                          ວັນທີອັບເດດລ່າສຸດ
                        </p>
                        <p className="font-semibold">
                          {formatDate(template.updatedAt)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Right Column - Sidebar */}
              <div className="space-y-6 lg:col-span-1">
                {/* Eligible Instructors Card */}
                <Card>
                  <CardContent className="px-6">
                    <div className="mb-4 flex items-center justify-between border-b pb-4">
                      <div className="flex items-center gap-2">
                        <UsersIcon className="size-5 text-red-600" />
                        <h3 className="font-semibold text-lg">
                          ຄູຜູ້ສອນທີ່ສາມາດສອນໄດ້
                        </h3>
                      </div>
                    </div>

                    {template.allowedInstructorIds &&
                      template.allowedInstructorIds.length > 0 ? (
                      <div className="space-y-3">
                        {eligibleInstructors?.map((instructor) => (
                          <div
                            key={instructor.id}
                            className="flex items-center gap-3 rounded-lg border p-3"
                          >
                            <Avatar className="h-10 w-10">
                              <AvatarFallback className="bg-red-100 text-red-600">
                                {getInitials(instructor.name)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <p className="font-semibold">{instructor.name}</p>
                              <p className="text-muted-foreground text-xs">
                                {instructor.employmentType === "full_time"
                                  ? "ປະຈຳ"
                                  : "ຟຣີແລນ"}
                              </p>
                            </div>
                            <CheckIcon className="size-5 text-blue-600" />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="py-8 text-center">
                        <UsersIcon className="mx-auto mb-2 size-8 text-muted-foreground/40" />
                        <p className="text-muted-foreground text-sm">
                          ບໍ່ໄດ້ກຳນົດ — ຜູ້ສອນທຸກຄົນສາມາດສອນໄດ້
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Template Tip Card */}
                {usageStats && usageStats.scheduled > 0 && (
                  <Card>
                    <CardContent className="px-6">
                      <div className="mb-4 flex items-center gap-2 border-b pb-4">
                        <LightbulbIcon className="size-5 text-red-600" />
                        <h3 className="font-semibold text-lg">ແນະນຳ</h3>
                      </div>
                      <p className="text-muted-foreground text-sm">
                        Template ນີ້ຖືກລ໊ອກເພື່ອການແກ້ໄຂຫຼັກເພາະມີ {usageStats.scheduled}{" "}
                        ຊົ່ວໂມງຮຽນຄລາດສ໌ ທີ່ຈອງແລ້ວ. ເພື່ອປ່ຽນແປງຂໍ້ມູນຫຼັກ, ກະລຸນາຈັດຕາຕະລາງ
                        ຊົ່ວໂມງຮຽນ ເຫຼົ່ານັ້ນກ່ອນ.
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          )}
        </QueryState>
      </Main>
    </>
  );
}
